import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface BusinessSetting {
  id: string;
  key: string;
  value: string;
  label: string;
  category: string;
  sort_order: number;
}

// Default values as fallbacks
const defaultSettings: Record<string, string> = {
  email: "hello@pixieclicks.com",
  phone: "+15551234567",
  phone_display: "(555) 123-4567",
  instagram_url: "https://instagram.com/pixieclicks",
  facebook_url: "https://facebook.com/pixieclicks",
  youtube_url: "https://youtube.com/@pixieclicks",
  tiktok_url: "https://tiktok.com/@pixieclicks",
  service_areas: "New Jersey, Florida & Colorado",
  business_name: "Pixie Clicks",
  tagline: "Capturing love stories across the nation",
  business_hours: "Mon-Fri: 9AM-6PM, Sat: 10AM-4PM, Sun: Closed",
};

export function useBusinessSettings() {
  const { data: settings = [], isLoading, error } = useQuery({
    queryKey: ["business-settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("business_settings")
        .select("*")
        .order("category")
        .order("sort_order");

      if (error) throw error;
      return data as BusinessSetting[];
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  const getSetting = (key: string): string => {
    const setting = settings.find((s) => s.key === key);
    return setting?.value ?? defaultSettings[key] ?? "";
  };

  const getSettingsByCategory = (category: string): BusinessSetting[] => {
    return settings.filter((s) => s.category === category);
  };

  return {
    settings,
    isLoading,
    error,
    getSetting,
    getSettingsByCategory,
  };
}
